/* ============================================================================
   COMBUSTIBLE — unidades y conversiones.

   Cuatro unidades, porque son las cuatro en las que el operador recibe la
   cifra según quién le sirva el combustible: litros y galones en la
   plataforma, kilos y libras en el peso y balance.

   Todo se guarda en LITROS y se convierte al mostrar. Guardar en la unidad
   elegida obligaría a reconvertir cada vez que se cambia de unidad, y cada
   reconversión arrastra error de redondeo: cargar 540 L, pasar a galones, a
   libras y volver a litros terminaría en 539,8.

   FACTORES
   --------
   Se usan los factores exactos, no los redondeados de bolsillo:

     1 galón US = 3,785411784 L   (exacto por definición)
     1 kg       = 2,20462262 lb   (exacto por definición del avoirdupois)

   Con 540 L y densidad 0,8 kg/L eso da 952,4 lb. Si en algún papel figuran
   950,2 lb es porque se usó el factor redondeado de 2,2: son 2,2 lb de
   diferencia, o sea 1 kg, que en peso y balance no es despreciable.
   ========================================================================== */

export const LITROS_POR_GALON_US = 3.785411784;
export const LIBRAS_POR_KG = 2.20462262;

export const UNIDADES_COMBUSTIBLE = [
  { clave: "L", etiqueta: "Litros", simbolo: "L", esMasa: false },
  { clave: "gal", etiqueta: "Galones US", simbolo: "gal US", esMasa: false },
  { clave: "kg", etiqueta: "Kilogramos", simbolo: "kg", esMasa: true },
  { clave: "lb", etiqueta: "Libras", simbolo: "lb", esMasa: true },
];

const esUnidad = (u) => UNIDADES_COMBUSTIBLE.some((x) => x.clave === u);
const densidadValida = (d) => Number.isFinite(d) && d > 0;

/**
 * Convierte una cantidad en litros a la unidad pedida.
 *
 * Las unidades de masa dependen de la DENSIDAD, que cambia con la temperatura
 * y con el tipo de combustible. Sin densidad válida no se puede pasar de
 * volumen a masa, y la función devuelve null en vez de suponer 0,8.
 */
export function desdeLitros(litros, unidad, densidadKgL) {
  if (!Number.isFinite(litros) || !esUnidad(unidad)) return null;
  switch (unidad) {
    case "L": return litros;
    case "gal": return litros / LITROS_POR_GALON_US;
    case "kg": return densidadValida(densidadKgL) ? litros * densidadKgL : null;
    case "lb": return densidadValida(densidadKgL) ? litros * densidadKgL * LIBRAS_POR_KG : null;
    default: return null;
  }
}

/** Convierte una cantidad expresada en `unidad` a litros. */
export function aLitros(valor, unidad, densidadKgL) {
  if (!Number.isFinite(valor) || !esUnidad(unidad)) return null;
  switch (unidad) {
    case "L": return valor;
    case "gal": return valor * LITROS_POR_GALON_US;
    case "kg": return densidadValida(densidadKgL) ? valor / densidadKgL : null;
    case "lb": return densidadValida(densidadKgL) ? valor / LIBRAS_POR_KG / densidadKgL : null;
    default: return null;
  }
}

/** La misma cantidad, en las cuatro unidades a la vez. */
export function enTodasLasUnidades(litros, densidadKgL) {
  return UNIDADES_COMBUSTIBLE.reduce((acc, u) => {
    acc[u.clave] = desdeLitros(litros, u.clave, densidadKgL);
    return acc;
  }, {});
}

/**
 * Porcentaje del combustible UTILIZABLE.
 *
 * Del utilizable, no del total del tanque: hay combustible que nunca llega al
 * motor y contarlo daría una autonomía que no existe.
 */
export function porcentajeDeCapacidad(litros, capacidadL) {
  if (!Number.isFinite(litros) || !Number.isFinite(capacidadL) || capacidadL <= 0) return null;
  return (litros / capacidadL) * 100;
}

/** Litros correspondientes a un porcentaje del utilizable, acotado a 0–100. */
export function litrosDesdePorcentaje(pct, capacidadL) {
  if (!Number.isFinite(pct) || !Number.isFinite(capacidadL) || capacidadL <= 0) return null;
  return (Math.max(0, Math.min(100, pct)) / 100) * capacidadL;
}

/**
 * ¿La cantidad cabe en el tanque?
 *
 * Se compara en litros, que es donde vive el dato. Comparar en la unidad
 * mostrada haría que 143 gal —que redondeado hacia arriba son 541,3 L— pasara
 * el control de un tanque de 540 L.
 */
export function cabeEnElTanque(litros, capacidadL) {
  if (!Number.isFinite(litros) || !Number.isFinite(capacidadL)) {
    return { cabe: false, excedeL: null, motivo: "Cantidad o capacidad sin definir." };
  }
  if (litros < 0) return { cabe: false, excedeL: null, motivo: "La cantidad no puede ser negativa." };
  if (litros > capacidadL) {
    return {
      cabe: false,
      excedeL: Math.round((litros - capacidadL) * 100) / 100,
      motivo: `Supera el utilizable de ${capacidadL} L en ${Math.round((litros - capacidadL) * 10) / 10} L.`,
    };
  }
  return { cabe: true, excedeL: 0, motivo: null };
}

/**
 * Consumo expresado en las cuatro unidades por hora.
 *
 * La carta del RFM da kg/h. El resto se deriva de ahí con la densidad; sin
 * densidad, las unidades de volumen quedan en null en vez de inventarse.
 */
export function consumoEnTodasLasUnidades(kgPorHora, densidadKgL) {
  if (!Number.isFinite(kgPorHora)) return { kg: null, lb: null, L: null, gal: null };
  const litrosPorHora = densidadValida(densidadKgL) ? kgPorHora / densidadKgL : null;
  return {
    kg: kgPorHora,
    lb: kgPorHora * LIBRAS_POR_KG,
    L: litrosPorHora,
    gal: litrosPorHora === null ? null : litrosPorHora / LITROS_POR_GALON_US,
  };
}
